import { Box, VStack, Text } from '@chakra-ui/react';
import type {
  ScriptElementFormState, ScriptLane, TagPickerDto, QuestionPickerDto, ScriptElementType,
} from '../../types/script'
import { ELEMENT_TYPE_COLORS } from '../../types/script'
import { BranchLane } from './BranchLane'
import { RejoinIndicator } from './RejoinIndicator'
import { findLaneForElement, getSourceElementLabel } from './laneUtils'

interface BranchSectionProps {
  branches: ScriptLane[]
  lanes: ScriptLane[]
  onAddElement: (laneId: string, type: ScriptElementType) => void
  onUpdateElement: (laneId: string, clientId: string, updates: Partial<ScriptElementFormState>) => void
  onRemoveElement: (laneId: string, clientId: string) => void
  onCreateBranch: (sourceClientId: string, branchType: string, branchKey: string, label: string) => void
  onDeleteLane: (laneId: string) => void
  onSetMergeTarget: (laneId: string, targetClientId: string | undefined) => void
  tagOptions: TagPickerDto[]
  questionOptions: QuestionPickerDto[]
}

export function BranchSection(props: BranchSectionProps) {
  const { branches, lanes } = props
  const sourceClientId = branches[0]?.sourceElementClientId
  const sourceLane = sourceClientId ? findLaneForElement(lanes, sourceClientId) : undefined
  const sourceEl = sourceLane?.elements.find((e) => e.clientId === sourceClientId)
  const ownerScheme = sourceEl ? ELEMENT_TYPE_COLORS[sourceEl.elementType] : 'gray'
  const ownerLabel = sourceClientId ? getSourceElementLabel(lanes, sourceClientId) : 'Branches'

  return (
    <Box
      data-testid="branch-section"
      position="relative"
      mt={2}
      ml={3}
      pl="30px"
      pr={3}
      py={3}
      borderWidth="1px"
      borderLeftWidth="3px"
      borderColor={`${ownerScheme}.200`}
      borderLeftColor={`${ownerScheme}.400`}
      borderRadius="14px"
      bg={`${ownerScheme}.50`}
    >
      {/* Vertical owner label down the rail (hidden on narrow via display prop) */}
      <Text
        data-testid="branch-owner-label"
        position="absolute"
        left="9px"
        top="14px"
        css={{
          writingMode: 'vertical-rl',
          transform: 'rotate(180deg)'
        }}
        fontSize="10px"
        fontWeight="bold"
        letterSpacing="0.14em"
        textTransform="uppercase"
        color={`${ownerScheme}.600`}
        display={{ base: 'none', md: 'block' }}
        pointerEvents="none"
        userSelect="none"
      >
        ↳ {ownerLabel}
      </Text>

      <VStack gap={3} align="stretch">
        {branches.map((branch) => (
          <BranchLane
            key={branch.id}
            branch={branch}
            lanes={lanes}
            onAddElement={props.onAddElement}
            onUpdateElement={props.onUpdateElement}
            onRemoveElement={props.onRemoveElement}
            onCreateBranch={props.onCreateBranch}
            onDeleteLane={props.onDeleteLane}
            onSetMergeTarget={props.onSetMergeTarget}
            tagOptions={props.tagOptions}
            questionOptions={props.questionOptions}
          />
        ))}
        <RejoinIndicator branches={branches} lanes={lanes} />
      </VStack>
    </Box>
  );
}
